import { inject, Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { ConfigService } from './config.service';
import { LoggerService } from './logger.service';
import {
  AnalyticEvent,
  AnalyticEventDibam,
  AnalyticEventDibamDelimiter,
  AnalyticEventDibamType,
  buildDibamEventPayload,
} from '../models/analytic-event';
import { CookiesService } from '../_core/state/dialogs/cookies/service/cookies.service';

declare let gtag: Function;

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private gTagId: string;
  private homeConfig: AnalyticEvent;
  private visitConfig: AnalyticEvent;
  private signupOpenConfig: AnalyticEvent;
  private signupOkConfig: AnalyticEvent;
  private checkoutStartConfig: AnalyticEvent;
  private checkoutEndConfig: AnalyticEvent;
  private holdStartConfig: AnalyticEvent;
  private holdEndConfig: AnalyticEvent;

  private readonly cookiesService = inject(CookiesService);

  constructor(
    private router: Router,
    private logger: LoggerService,
    private configService: ConfigService
  ) {
  }

  public init() {
    if (this.configService.initError) {
      return;
    }

    if (this.configService.devMode) {
      this.logger.info('No analytics for you (development environment)');
      return;
    }

    this.gTagId = this.configService.get().googleAnalytics;

    if (!this.gTagId || !this.cookiesService.hasConsentForCookie('ga')) {
      return this.logger.warn('No analytics for you');
    }

    const eventsConfig = this.configService.get().analyticEvents;

    if (eventsConfig) {
      this.homeConfig = eventsConfig.find((c) => c.type === 'HOME');
      this.visitConfig = eventsConfig.find((c) => c.type === 'VISIT');
      this.signupOpenConfig = eventsConfig.find((c) => c.type === 'SIGNUP_OPEN');
      this.signupOkConfig = eventsConfig.find((c) => c.type === 'SIGNUP_OK');
      this.checkoutStartConfig = eventsConfig.find((c) => c.type === 'CHECKOUT_START');
      this.checkoutEndConfig = eventsConfig.find((c) => c.type === 'CHECKOUT_END');
      this.holdStartConfig = eventsConfig.find((c) => c.type === 'HOLD_START');
      this.holdEndConfig = eventsConfig.find((c) => c.type === 'HOLD_END');
    }

    this.listenForRouteChanges();

    const gtagScript = document.createElement('script');
    gtagScript.async = true;
    gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=' + this.gTagId;
    document.head.appendChild(gtagScript);

    const dataLayerScript = document.createElement('script');
    dataLayerScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag() { dataLayer.push(arguments); }
        gtag('js', new Date());
        gtag('config', '${this.gTagId}', {'send_page_view': false});`;

    document.head.appendChild(dataLayerScript);
  }

  private listenForRouteChanges() {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (event.urlAfterRedirects === '/') {
          this.sendVisitHomeEvent();
        } else {
          this.sendVisitEvent(event);
        }
      }
    });
  }

  public sendVisitHomeEvent() {
    const event = this.homeConfig ? this.homeConfig : { action: 'visit home', category: null, label: null };

    this.sendEvent(event);
  }

  public sendVisitEvent(visitEvent: NavigationEnd) {
    // TODO: migrar nuestros eventos a la manera correcta de enviarlos (visitConfig)
    gtag('config', this.gTagId, { page_path: visitEvent.urlAfterRedirects });
    this.logger.info('Sending Google Analytics hit for route', visitEvent.urlAfterRedirects);
    this.logger.info('Property ID', this.gTagId);
  }

  public sendOpenSignupEvent() {
    if (this.configService.isClientDibam()) {
      this.sendEventDibam({
        delimiter: AnalyticEventDibamDelimiter.START,
        type: AnalyticEventDibamType.SIGNUP,
      });
      return;
    }

    if (this.signupOpenConfig) {
      this.sendEvent(this.signupOpenConfig);
      return;
    }

    this.sendEvent({
      action: 'signup open',
      category: null,
      label: null,
    });
  }

  public sendSignupOkEvent() {
    if (this.configService.isClientDibam()) {
      this.sendEventDibam({
        delimiter: AnalyticEventDibamDelimiter.END,
        type: AnalyticEventDibamType.SIGNUP,
      });
      return;
    }

    if (this.signupOkConfig) {
      this.sendEvent(this.signupOkConfig);
      return;
    }

    this.sendEvent({
      action: 'signup ok',
      category: null,
      label: null,
    });
  }

  public sendCheckoutStartEvent() {
    if (this.configService.isClientDibam()) {
      this.sendEventDibam({
        delimiter: AnalyticEventDibamDelimiter.START,
        type: AnalyticEventDibamType.CHECKOUT,
      });
      return;
    }

    if (this.checkoutStartConfig) {
      this.sendEvent(this.checkoutStartConfig);
      return;
    }

    this.sendEvent({
      action: 'checkout start',
      category: null,
      label: null,
    });
  }

  public sendCheckoutEndEvent() {
    if (this.configService.isClientDibam()) {
      this.sendEventDibam({
        delimiter: AnalyticEventDibamDelimiter.END,
        type: AnalyticEventDibamType.CHECKOUT,
      });
      return;
    }

    if (this.checkoutEndConfig) {
      this.sendEvent(this.checkoutEndConfig);
      return;
    }

    this.sendEvent({
      action: 'checkout end',
      category: null,
      label: null,
    });
  }

  public sendHoldStartEvent() {
    if (this.configService.isClientDibam()) {
      this.sendEventDibam({
        delimiter: AnalyticEventDibamDelimiter.START,
        type: AnalyticEventDibamType.HOLD,
      });
      return;
    }

    if (this.holdStartConfig) {
      this.sendEvent(this.holdStartConfig);
      return;
    }

    this.sendEvent({
      action: 'hold start',
      category: null,
      label: null,
    });
  }

  public sendHoldEndEvent() {
    if (this.configService.isClientDibam()) {
      this.sendEventDibam({
        delimiter: AnalyticEventDibamDelimiter.END,
        type: AnalyticEventDibamType.HOLD,
      });
      return;
    }

    if (this.holdEndConfig) {
      this.sendEvent(this.holdEndConfig);
      return;
    }
    this.sendEvent({
      action: 'hold end',
      category: null,
      label: null,
    });
  }

  private sendEvent(event: AnalyticEvent) {
    if (typeof gtag === 'undefined') {
      return;
    }

    gtag('event', event.action, {
      event_category: event.category,
      event_label: event.label,
      value: event.value,
    });

    this.logEvent(event);
  }

  private sendEventDibam(event: AnalyticEventDibam) {
    if (typeof gtag === 'undefined') {
      return;
    }

    gtag('event', buildDibamEventPayload(event.delimiter, event.type), {});
    this.logEvent(event);
  }

  private logEvent(event) {
    this.logger.info('Sending event to Google Analytics', event);
    this.logger.info('Property ID', this.gTagId);
  }
}
